import * as IF from "infrontjs";
import Swal from 'sweetalert2/dist/sweetalert2.js';
import template from './template.html';
import { generateApiExamples } from './api-examples.mjs';

class TestCenter extends HTMLElement
{
    constructor()
    {
        super();
        this._isConnected = false;
        this._template = null;
        this._arrayCounters = {}; // Track number of items for each array
    }

    connectedCallback()
    {
        if ( false === this._isConnected )
        {
            this.app = IF.App.get();
            this.render();

            this._isConnected = true;
        }
    }

    get template()
    {
        return this._template;
    }

    set template( val )
    {
        this._template = val;
        this._arrayCounters = {}; // Reset counters when template changes
        if ( this._isConnected )
        {
            this.render();
        }
    }

    _getPlaceholders()
    {
        if ( !this._template || !this._template.data || !this._template.data.placeholders )
        {
            return [];
        }

        const placeholders = this._template.data.placeholders;

        // Handle both array and object formats
        const placeholderList = Array.isArray( placeholders )
            ? placeholders
            : Object.keys( placeholders ).map( key => ({
                key: placeholders[ key ].key || key,
                ...placeholders[ key ]
            }));

        return placeholderList.map( p => ({
            key: p.key,
            description: p.description || null,
            type: p.type || 'string',
            options: p.options || {},
            condition: p.condition || null,
            loopItems: p.loopItems || []
        }));
    }

    /**
     * Get a human-readable label for a placeholder type
     */
    _getTypeLabel( type )
    {
        const labels = {
            'string': 'Text',
            'number': 'Number',
            'date': 'Date',
            'boolean': 'Yes/No',
            'image': 'Image',
            'array': 'List'
        };
        return labels[ type ] || 'Text';
    }

    /**
     * Get a hint based on options
     */
    _getOptionsHint( placeholder )
    {
        const hints = [];
        const options = placeholder.options || {};

        if ( options.format )
        {
            if ( options.format === 'currency' )
            {
                hints.push( `Currency (${options.currency || 'EUR'})` );
            }
            else if ( options.format === 'percent' )
            {
                hints.push( 'Percentage' );
            }
            else if ( /[YMDHms]/.test( options.format ) )
            {
                hints.push( `Date format: ${options.format}` );
            }
        }

        if ( options.transform )
        {
            hints.push( `Transform: ${options.transform}` );
        }

        if ( options.default )
        {
            hints.push( `Default: ${options.default}` );
        }

        if ( options.width || options.height || options.maxWidth || options.maxHeight )
        {
            const dims = [];
            if ( options.width ) dims.push( `width: ${options.width}` );
            if ( options.height ) dims.push( `height: ${options.height}` );
            if ( options.maxWidth ) dims.push( `max-width: ${options.maxWidth}` );
            if ( options.maxHeight ) dims.push( `max-height: ${options.maxHeight}` );
            hints.push( dims.join( ', ' ) );
        }

        return hints.join( ' | ' );
    }

    /**
     * Initialize default array counters
     */
    _initArrayCounters()
    {
        const placeholders = this._getPlaceholders();
        placeholders.forEach( p => {
            if ( p.type === 'array' && !this._arrayCounters[ p.key ] )
            {
                this._arrayCounters[ p.key ] = 1; // Start with 1 item
            }
        });
    }

    render()
    {
        if ( !this.app?.view )
        {
            return;
        }

        this._initArrayCounters();

        const placeholders = this._getPlaceholders();

        this.app.view.render(
            this,
            template,
            {
                placeholders: placeholders,
                arrayCounters: this._arrayCounters,
                getTypeLabel: this._getTypeLabel.bind( this ),
                getOptionsHint: this._getOptionsHint.bind( this )
            }
        );

        this._bindEvents();
    }

    _bindEvents()
    {
        // Run test button
        const runButton = this.querySelector( '[data-type="test-center-run"]' );
        if ( runButton )
        {
            runButton.addEventListener( 'click', () => {
                this._onRunTest();
            });
        }

        // Run HTML test button
        const runHtmlButton = this.querySelector( '[data-type="test-center-run-html"]' );
        if ( runHtmlButton )
        {
            runHtmlButton.addEventListener( 'click', () => {
                this._onRunHtmlTest();
            });
        }

        // Show example code button
        const codeButton = this.querySelector( '[data-type="test-center-show-code"]' );
        if ( codeButton )
        {
            codeButton.addEventListener( 'click', () => {
                this._onShowExampleCode();
            });
        }

        // Add array item buttons
        const addButtons = this.querySelectorAll( '[data-action="add-array-item"]' );
        addButtons.forEach( btn => {
            btn.addEventListener( 'click', ( e ) => {
                const arrayKey = e.currentTarget.dataset.arrayKey;
                this._addArrayItem( arrayKey );
            });
        });

        // Remove array item buttons
        const removeButtons = this.querySelectorAll( '[data-action="remove-array-item"]' );
        removeButtons.forEach( btn => {
            btn.addEventListener( 'click', ( e ) => {
                const arrayKey = e.currentTarget.dataset.arrayKey;
                this._removeArrayItem( arrayKey );
            });
        });

        // Image file inputs - show preview
        const imageInputs = this.querySelectorAll( 'input[type="file"][data-type="image"]' );
        imageInputs.forEach( input => {
            input.addEventListener( 'change', ( e ) => {
                this._handleImagePreview( e.target );
            });
        });
    }

    _addArrayItem( arrayKey )
    {
        this._arrayCounters[ arrayKey ] = ( this._arrayCounters[ arrayKey ] || 1 ) + 1;
        this.render();
    }

    _removeArrayItem( arrayKey )
    {
        if ( this._arrayCounters[ arrayKey ] > 1 )
        {
            this._arrayCounters[ arrayKey ]--;
            this.render();
        }
    }

    _handleImagePreview( input )
    {
        const previewId = `preview-${input.name}`;
        let preview = this.querySelector( `#${previewId}` );

        if ( input.files && input.files[ 0 ] )
        {
            const reader = new FileReader();
            reader.onload = ( e ) => {
                if ( preview )
                {
                    preview.src = e.target.result;
                    preview.style.display = 'block';
                }
            };
            reader.readAsDataURL( input.files[ 0 ] );
        }
        else if ( preview )
        {
            preview.style.display = 'none';
        }
    }

    /**
     * Collect all form data and build the variables object
     */
    _collectFormData()
    {
        const variables = {};
        const images = {};
        const placeholders = this._getPlaceholders();

        placeholders.forEach( p => {
            if ( p.type === 'array' )
            {
                // Collect array items
                const count = this._arrayCounters[ p.key ] || 1;
                const items = [];

                for ( let i = 0; i < count; i++ )
                {
                    const item = {};
                    ( p.loopItems || [] ).forEach( prop => {
                        const input = this.querySelector( `[name="${p.key}[${i}].${prop.key}"]` );
                        if ( input )
                        {
                            item[ prop.key ] = this._parseInputValue( input, prop.type );
                        }
                    });
                    items.push( item );
                }

                variables[ p.key ] = items;
            }
            else if ( p.type === 'image' )
            {
                const input = this.querySelector( `input[name="${p.key}"]` );
                if ( input && input.files && input.files[ 0 ] )
                {
                    images[ p.key ] = input.files[ 0 ];
                }
            }
            else
            {
                const input = this.querySelector( `[name="${p.key}"]` );
                if ( input )
                {
                    variables[ p.key ] = this._parseInputValue( input, p.type );
                }
            }
        });

        return { variables, images };
    }

    /**
     * Parse input value based on expected type
     */
    _parseInputValue( input, type )
    {
        const value = input.type === 'checkbox' ? input.checked : input.value;

        switch ( type )
        {
            case 'number':
                return value === '' ? null : parseFloat( value );

            case 'boolean':
                if ( input.type === 'checkbox' )
                {
                    return input.checked;
                }
                return value === 'true' || value === '1' || value === 'yes';

            case 'date':
                return value || null;

            default:
                return value;
        }
    }

    async _onRunTest()
    {
        if ( !this._template )
        {
            return;
        }

        const { variables, images } = this._collectFormData();

        const pageContent = this.app.view.pageContent;
        this.app.view.blockUI( pageContent, 'Preparing document...' );

        try
        {
            const formData = new FormData();
            formData.append( 'data', JSON.stringify({
                templateId: this._template.id,
                variables: variables,
                metadataOnly: true
            }));

            Object.keys( images ).forEach( key => {
                formData.append( `image_${key}`, images[ key ] );
            });

            const metadataResponse = await fetch( `${this.app.api.getUrl()}/generation/pdf`, {
                method: 'POST',
                headers: {
                    'x-sessiontoken': this.app.api._api.headers.get( 'x-sessiontoken' ),
                    'x-version': this.app.api._api.headers.get( 'x-version' )
                },
                body: formData
            });

            this.app.view.unblockUI( pageContent );

            if ( !metadataResponse.ok )
            {
                throw new Error( `Document preparation failed: ${metadataResponse.statusText}` );
            }

            const metadata = await metadataResponse.json();

            const result = await Swal.fire({
                title: 'Document Ready',
                html: this._buildMetadataHtml( metadata ),
                showCancelButton: true,
                confirmButtonText: '<i class="ti ti-download me-1"></i> Download',
                cancelButtonText: 'Cancel',
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#6c757d'
            });

            if ( result.isConfirmed )
            {
                await this._downloadDocument( metadata.filename );
            }
        }
        catch ( e )
        {
            console.error( e );
            this.app.view.unblockUI( pageContent );
            this.app.view.toast( 'Failed to prepare document', 'error' );
        }
    }

    async _onRunHtmlTest()
    {
        if ( !this._template )
        {
            return;
        }

        const { variables, images } = this._collectFormData();

        const pageContent = this.app.view.pageContent;
        this.app.view.blockUI( pageContent, 'Generating HTML document...' );

        try
        {
            const formData = new FormData();
            formData.append( 'data', JSON.stringify({
                templateId: this._template.id,
                variables: variables,
                metadataOnly: true
            }));

            Object.keys( images ).forEach( key => {
                formData.append( `image_${key}`, images[ key ] );
            });

            const metadataResponse = await fetch( `${this.app.api.getUrl()}/generation/html`, {
                method: 'POST',
                headers: {
                    'x-sessiontoken': this.app.api._api.headers.get( 'x-sessiontoken' ),
                    'x-version': this.app.api._api.headers.get( 'x-version' )
                },
                body: formData
            });

            this.app.view.unblockUI( pageContent );

            if ( !metadataResponse.ok )
            {
                throw new Error( `HTML generation failed: ${metadataResponse.statusText}` );
            }

            const metadata = await metadataResponse.json();

            const result = await Swal.fire({
                title: 'HTML Document Ready',
                html: this._buildMetadataHtml( metadata ),
                showCancelButton: true,
                confirmButtonText: '<i class="ti ti-download me-1"></i> Download',
                cancelButtonText: 'Cancel',
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#6c757d'
            });

            if ( result.isConfirmed )
            {
                await this._downloadDocument( metadata.filename );
            }
        }
        catch ( e )
        {
            console.error( e );
            this.app.view.unblockUI( pageContent );
            this.app.view.toast( 'Failed to generate HTML document', 'error' );
        }
    }

    _buildMetadataHtml( metadata )
    {
        const items = [];

        if ( metadata.filename )
        {
            items.push( `<div class="mb-2"><strong>Filename:</strong> ${metadata.filename}</div>` );
        }

        if ( metadata.templateName )
        {
            items.push( `<div class="mb-2"><strong>Template:</strong> ${metadata.templateName}</div>` );
        }

        if ( metadata.placeholdersCount !== undefined )
        {
            items.push( `<div class="mb-2"><strong>Placeholders:</strong> ${metadata.placeholdersCount}</div>` );
        }

        if ( metadata.generatedAt )
        {
            items.push( `<div class="mb-2"><strong>Generated:</strong> ${new Date( metadata.generatedAt ).toLocaleString()}</div>` );
        }

        if ( items.length === 0 )
        {
            items.push( '<div class="text-muted">Document is ready for download.</div>' );
        }

        return `<div class="text-start">${items.join( '' )}</div>`;
    }

    _onShowExampleCode()
    {
        if ( !this._template )
        {
            return;
        }

        const { variables } = this._collectFormData();
        const templateId = this._template.id;
        const apiUrl = this.app.api.getUrl();
        const endpoint = `${apiUrl}/generation/pdf`;

        const tabs = generateApiExamples( endpoint, templateId, variables );

        const tabHeaders = tabs.map( ( tab, i ) =>
            `<li class="nav-item">
                <a href="javascript:void(0)" class="nav-link gap-6 note-link d-flex align-items-center justify-content-center px-3 px-md-3${i === 0 ? ' active' : ''}" data-tab="${tab.id}">${tab.label}</a>
            </li>`
        ).join( '' );

        const tabContents = tabs.map( ( tab, i ) =>
            `<div class="tab-pane${i === 0 ? ' active' : ''}" data-tab-content="${tab.id}">
                <pre class="mb-0" style="max-height: 400px; overflow: auto; text-align: left; background: #1e1e1e; color: #d4d4d4; padding: 1rem; border-radius: 0.375rem; font-size: 0.8rem; white-space: pre-wrap; word-break: break-all;"><code>${this._escapeHtml( tab.code )}</code></pre>
            </div>`
        ).join( '' );

        const html = `
            <ul class="nav nav-pills p-3 mb-3 rounded align-items-center card flex-row" id="codeExampleTabs">${tabHeaders}</ul>
            <div class="tab-content" id="codeExampleContent">${tabContents}</div>
        `;

        Swal.fire({
            title: 'API Example Code',
            html: html,
            width: '700px',
            showConfirmButton: false,
            showCloseButton: true,
            showCancelButton: true,
            cancelButtonText: 'Close',
            cancelButtonColor: '#6c757d',
            didOpen: ( popup ) => {
                // Hide inactive tab panes
                popup.querySelectorAll( '.tab-pane:not(.active)' ).forEach( p => p.style.display = 'none' );
                const tabButtons = popup.querySelectorAll( '[data-tab]' );
                tabButtons.forEach( btn => {
                    btn.addEventListener( 'click', () => {
                        // Deactivate all
                        popup.querySelectorAll( '.nav-link' ).forEach( b => b.classList.remove( 'active' ) );
                        popup.querySelectorAll( '.tab-pane' ).forEach( p => { p.classList.remove( 'active' ); p.style.display = 'none'; } );
                        // Activate selected
                        btn.classList.add( 'active' );
                        const pane = popup.querySelector( `[data-tab-content="${btn.dataset.tab}"]` );
                        pane.classList.add( 'active' );
                        pane.style.display = 'block';
                    });
                });
            }
        });
    }

    _escapeHtml( str )
    {
        return str.replace( /&/g, '&amp;' ).replace( /</g, '&lt;' ).replace( />/g, '&gt;' ).replace( /"/g, '&quot;' );
    }

    async _downloadDocument( filename )
    {
        const pageContent = this.app.view.pageContent;
        this.app.view.blockUI( pageContent, 'Downloading document...' );

        try
        {
            const response = await fetch( `${this.app.api.getUrl()}/generation/document/${this._template.id}/${filename}`, {
                method: 'GET',
                headers: {
                    'x-sessiontoken': this.app.api._api.headers.get( 'x-sessiontoken' ),
                    'x-version': this.app.api._api.headers.get( 'x-version' )
                }
            });

            if ( !response.ok )
            {
                throw new Error( `Document download failed: ${response.statusText}` );
            }

            const blob = await response.blob();
            const url = URL.createObjectURL( blob );
            const a = document.createElement( 'a' );
            a.href = url;
            a.download = filename;
            document.body.appendChild( a );
            a.click();
            document.body.removeChild( a );
            URL.revokeObjectURL( url );

            this.app.view.unblockUI( pageContent );
            this.app.view.toast( 'Document downloaded successfully', 'success' );
        }
        catch ( e )
        {
            console.error( e );
            this.app.view.unblockUI( pageContent );
            this.app.view.toast( 'Failed to generate document', 'error' );
        }
    }
}

customElements.define( 'test-center', TestCenter );
